import {
  Button,
  Col,
  humanReadableAddress,
  Menu,
  Row,
  Tab,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideTrash2 } from 'lucide-react';

import {
  $currentLocation,
  $deleteFacility,
  $facilityId,
} from '../../_state/locations.state';
import {
  matchesSettingsRoute,
  useSettingsLocation,
} from '../../_state/settingsModule.state';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';

import { LocationDocumentTemplates } from './location-document-templates/_LocationDocumentTemplates';
import { LocationPreferences } from './location-preferences/_LocationPreferences';
import { LocationInfo } from './LocationInfo';

import { LocationFreightPreferences } from '~/modules/trade/_components/_shared/LocationFreightPreferences';

export const LocationDetailsView = () => {
  const toast = useToast();
  const [location, setLocation] = useSettingsLocation();
  const currentLocation = useAtomValue($currentLocation);
  const facilityId = useAtomValue($facilityId);
  const deleteFacility = useSetAtom($deleteFacility);

  const handleDelete = async () => {
    if (!currentLocation?.id) return;

    try {
      await deleteFacility({ id: currentLocation.id });
      toast.success('Location deleted successfully');
      setLocation('/locations/facilities');
    } catch (error) {
      console.error('Failed to delete facility:', error);
      toast.error('Failed to delete location');
    }
  };

  const isLocationInfo = matchesSettingsRoute(
    location,
    '/locations/facilities/:facilityId/info',
  );
  const isLocationPreferences = matchesSettingsRoute(
    location,
    '/locations/facilities/:facilityId/preferences',
  );
  const isLocationDocumentTemplates = matchesSettingsRoute(
    location,
    '/locations/facilities/:facilityId/document-templates',
  );
  const isLocationFreight = matchesSettingsRoute(
    location,
    '/locations/facilities/:facilityId/freight',
  );

  return (
    <SettingsSubmodule
      className={'@LocationDetailsView h-full'}
      contentClassName="p-0"
      title={currentLocation?.name ?? 'Loading...'}
      subtitle={
        currentLocation?.address
          ? humanReadableAddress(currentLocation.address)
          : '...'
      }
      tools={
        <Menu
          trigger={
            <Button variant="outline" color="failure" icon={<LucideTrash2 />}>
              Delete
            </Button>
          }
          items={[
            {
              label: 'Delete location',
              icon: <LucideTrash2 />,
              className: 'text-failure',
              secondStep: ({ close }) => (
                <Col gap3>
                  <div className="text-center text-sm text-neutral/70">
                    Are you sure you want to delete this location?
                  </div>
                  <Row gap2>
                    <Button variant="outline" color="neutral" onPress={close}>
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      color="failure"
                      icon={<LucideTrash2 />}
                      onPress={() => {
                        close();
                        handleDelete();
                      }}
                    >
                      Yes, delete
                    </Button>
                  </Row>
                </Col>
              ),
            },
          ]}
        />
      }
      onBackPressed={() => setLocation('/locations/facilities')}
      tabs={
        <>
          <Tab
            selected={isLocationInfo}
            onClick={() =>
              setLocation(`/locations/facilities/${facilityId}/info`)
            }
          >
            Location Info
          </Tab>
          <Tab
            selected={isLocationPreferences}
            onClick={() =>
              setLocation(`/locations/facilities/${facilityId}/preferences`)
            }
          >
            Preferences
          </Tab>
          <Tab
            selected={isLocationDocumentTemplates}
            onClick={() =>
              setLocation(
                `/locations/facilities/${facilityId}/document-templates`,
              )
            }
          >
            Document Templates
          </Tab>
          <Tab
            selected={isLocationFreight}
            onClick={() =>
              setLocation(`/locations/facilities/${facilityId}/freight`)
            }
          >
            Freight
          </Tab>
        </>
      }
    >
      {isLocationInfo && <LocationInfo />}
      {isLocationPreferences && <LocationPreferences />}
      {isLocationDocumentTemplates && <LocationDocumentTemplates />}
      {isLocationFreight && (
        <LocationFreightPreferences locationId={facilityId ?? ''} />
      )}
    </SettingsSubmodule>
  );
};
